import { getRegionMapAreas } from '../coordinates';

export const getBourgogneData = () => ({
  name: 'Bourgogne',
  displayName: 'Bourgogne',
  country: 'France',
  description: 'La Bourgogne est le berceau des vins de terroir, célèbre pour ses vins rouges de Pinot Noir et ses vins blancs de Chardonnay d\'exception.',
  longDescription: 'La Bourgogne, située au cœur de la France, est l\'une des régions viticoles les plus prestigieuses au monde. Cette région historique produit des vins d\'une finesse et d\'une complexité exceptionnelles, principalement à base de Pinot Noir pour les rouges et de Chardonnay pour les blancs. La Bourgogne est le modèle par excellence de l\'expression du terroir, où chaque parcelle, chaque climat, exprime sa propre identité.',
  
  // Map areas for InteractiveWineMap
  mapAreas: getRegionMapAreas('Bourgogne'),
  
  // Map coordinates for Bourgogne
  map: {
    coordinates: [47.0525, 4.3833],
    zoom: 8
  },
  
  // Key statistics
  statistics: {
    surfaceArea: '29,500 hectares',
    annualProduction: '1.5 millions d\'hectolitres',
    numberOfProducers: '4,000 domaines',
    averageAltitude: '200-400 mètres',
    soilTypes: 'Argilo-calcaire, marnes, calcaires'
  },
  
  // Climate information
  climate: {
    type: 'Continental tempéré',
    description: 'Climat continental avec des hivers froids et des étés chauds, mais sans excès. Les précipitations sont bien réparties tout au long de l\'année.',
    characteristics: [
      'Hivers froids mais pas excessifs',
      'Étés chauds et ensoleillés',
      'Automnes secs favorables à la maturation',
      'Précipitations régulières',
      'Amplitudes thermiques importantes'
    ]
  },
  
  // Grape varieties
  grapeVarieties: {
    red: [
      {
        name: 'Pinot Noir',
        percentage: '35%',
        description: 'Le cépage roi de la Bourgogne, produisant des vins d\'une finesse et d\'une élégance exceptionnelles.',
        characteristics: 'Fruits rouges, épices, notes de sous-bois, tanins soyeux'
      }
    ],
    white: [
      {
        name: 'Chardonnay',
        percentage: '48%',
        description: 'Le cépage blanc dominant, exprimant magnifiquement la diversité des terroirs bourguignons.',
        characteristics: 'Fruits blancs, beurre, notes minérales, acidité vive'
      },
      {
        name: 'Aligoté',
        percentage: '6%',
        description: 'Cépage traditionnel produisant des vins secs et vifs.',
        characteristics: 'Acidité vive, notes d\'agrumes, fraîcheur'
      }
    ]
  },
  
  // Wine styles
  wineStyles: {
    red: [
      {
        name: 'Bourgogne Rouge',
        description: 'Vins rouges élégants et fruités, expression du Pinot Noir.',
        characteristics: 'Fruits rouges, épices douces, tanins soyeux, acidité vive',
        aging: '3-8 ans',
        serving: '14-16°C'
      },
      {
        name: 'Grands Crus Rouges',
        description: 'Les vins les plus prestigieux, d\'une complexité et d\'une longévité exceptionnelles.',
        characteristics: 'Complexité aromatique, structure imposante, tanins nobles, potentiel de garde',
        aging: '10-30 ans',
        serving: '16-18°C'
      }
    ],
    white: [
      {
        name: 'Bourgogne Blanc',
        description: 'Vins blancs secs et élégants, expression du Chardonnay.',
        characteristics: 'Fruits blancs, notes minérales, acidité vive, fraîcheur',
        aging: '3-10 ans',
        serving: '12-14°C'
      },
      {
        name: 'Grands Crus Blancs',
        description: 'Les vins blancs les plus complexes et les plus longs à la garde.',
        characteristics: 'Richesse aromatique, beurre, notes de fruits secs, complexité',
        aging: '8-25 ans',
        serving: '12-14°C'
      }
    ]
  },
  
  // Subregions
  subregions: {
    chablis: {
      name: 'Chablis',
      slug: 'chablis',
      description: 'Région septentrionale produisant des vins blancs de Chardonnay d\'une pureté exceptionnelle.',
      characteristics: 'Vins blancs secs, minéraux, acidité vive, notes de pierre à fusil',
      grapeVarieties: {
        white: ['Chardonnay']
      },
      wineStyles: {
        white: 'Vins blancs secs et minéraux avec une acidité vive caractéristique'
      },
      foodPairing: 'Fruits de mer, poissons nobles, fromages de chèvre',
      map: {
        coordinates: [47.8167, 3.8000],
        zoom: 10
      }
    },
    'cote-de-nuits': {
      name: 'Côte de Nuits',
      slug: 'cote-de-nuits',
      description: 'Le cœur des grands vins rouges de Bourgogne, avec les plus prestigieux Grands Crus.',
      characteristics: 'Vins rouges puissants, structurés, tanins nobles, complexité aromatique',
      grapeVarieties: {
        red: ['Pinot Noir'],
        white: ['Chardonnay']
      },
      wineStyles: {
        red: 'Vins rouges puissants et structurés avec des tanins nobles',
        white: 'Vins blancs complexes et longs à la garde'
      },
      foodPairing: 'Viandes rouges, gibier, fromages affinés',
      map: {
        coordinates: [47.2500, 4.9500],
        zoom: 10
      }
    },
    'cote-de-beaune': {
      name: 'Côte de Beaune',
      slug: 'cote-de-beaune',
      description: 'Région de transition entre vins rouges et blancs, célèbre pour ses Grands Crus blancs.',
      characteristics: 'Vins rouges élégants et vins blancs complexes, équilibre parfait',
      grapeVarieties: {
        red: ['Pinot Noir'],
        white: ['Chardonnay']
      },
      wineStyles: {
        red: 'Vins rouges élégants et fruités',
        white: 'Vins blancs complexes et riches'
      },
      foodPairing: 'Volailles, poissons nobles, fromages doux',
      map: {
        coordinates: [46.7833, 4.8500],
        zoom: 10
      }
    },
    'cote-chalonnaise': {
      name: 'Côte Chalonnaise',
      slug: 'cote-chalonnaise',
      description: 'Région méridionale produisant des vins de qualité à prix abordables.',
      characteristics: 'Vins rouges et blancs équilibrés, bon rapport qualité-prix',
      grapeVarieties: {
        red: ['Pinot Noir'],
        white: ['Chardonnay', 'Aligoté']
      },
      wineStyles: {
        red: 'Vins rouges équilibrés et accessibles',
        white: 'Vins blancs secs et vifs'
      },
      foodPairing: 'Cuisine traditionnelle, fromages locaux',
      map: {
        coordinates: [46.7500, 4.8500],
        zoom: 10
      }
    },
    maconnais: {
      name: 'Mâconnais',
      slug: 'maconnais',
      description: 'Région la plus méridionale, spécialisée dans les vins blancs de Chardonnay.',
      characteristics: 'Vins blancs fruités, accessibles, notes de fruits blancs',
      grapeVarieties: {
        white: ['Chardonnay', 'Aligoté']
      },
      wineStyles: {
        white: 'Vins blancs fruités et accessibles'
      },
      foodPairing: 'Cuisine légère, poissons, volailles',
      map: {
        coordinates: [46.3000, 4.8333],
        zoom: 10
      }
    }
  },
  
  // Food pairings
  foodPairings: {
    red: [
      'Viandes rouges grillées',
      'Gibier (chevreuil, sanglier)',
      'Fromages affinés (Époisses, Comté)',
      'Plats en sauce',
      'Charcuteries fines'
    ],
    white: [
      'Poissons nobles (sole, turbot)',
      'Crustacés',
      'Volailles en sauce',
      'Fromages doux',
      'Cuisine à la crème'
    ]
  },
  
  // History
  history: 'La viticulture en Bourgogne remonte à l\'époque romaine, mais c\'est au Moyen Âge que les moines cisterciens et bénédictins ont véritablement structuré la viticulture bourguignonne. Ils ont identifié et délimité les meilleurs terroirs, créant ainsi le concept de "climat" qui est unique à la Bourgogne. Au fil des siècles, la région a développé une hiérarchie des appellations (Régionale, Village, Premier Cru, Grand Cru) qui reste la référence mondiale pour l\'expression du terroir.',
  
  // Tourism
  tourism: {
    highlights: [
      'Route des Grands Crus',
      'Caves historiques de Beaune',
      'Hospices de Beaune',
      'Châteaux viticoles',
      'Marchés aux vins'
    ],
    bestTimeToVisit: 'Avril à octobre',
    activities: [
      'Dégustations dans les domaines',
      'Visites de caves',
      'Randonnées viticoles',
      'Festivals de vins',
      'Cours d\'œnologie'
    ]
  }
}); 